import * as React from 'react';
import { ExpansionPanel, ExpansionPanelSummary, Typography, ExpansionPanelDetails, makeStyles, Button, Snackbar, SnackbarContent } from '@material-ui/core';
import { useIifImportSelector, IifImportSelectors, IifImportActions } from '../store/modules/iifImport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { faCheckCircle } from '@fortawesome/pro-duotone-svg-icons/faCheckCircle';
import { faSync } from '@fortawesome/pro-duotone-svg-icons/faSync';
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons/faExclamationCircle';
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons/faSpinner';
import BillsView from './BillsView';
import { classNames } from '../shared';
import { useDispatch } from 'react-redux';
import { RequestState } from '../store/modules';
import { green } from '@material-ui/core/colors';
import ExpensesView from './ExpensesView';
import SalesReceiptsView from './SalesReceiptsView';
import ErrorView from './ErrorView';
import DepositsView from './DepositsView';
import JournalEntriesView from './JournalEntriesView';
import WarningView from './WarningView';

interface IIifFileViewProps {
    fileIndex: number;
}

const useStyles = makeStyles(theme => ({
    invalid: {
        background: theme.palette.grey[100]
    },
    contentContainer: {
        width: '100%'
    },
    buttonContainer: {
        textAlign: 'right',
        paddingTop: theme.spacing(3)
    },
    success: {
        backgroundColor: green[600],
    },
    error: {
        backgroundColor: theme.palette.error.dark,
        overflow: 'auto'
    },
    message: {
        display: 'flex',
        alignItems: 'center',
    },
    messageIcon: {
        marginRight: theme.spacing(4)
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1
    },
    headerIcon: {
        marginLeft: 'auto'
    },
    headerRefresh: {
        '--fa-primary-color': theme.palette.primary.main,
        '--fa-secondary-color': theme.palette.primary.main
    },
    headerSuccess: {
        '--fa-primary-color': green[600],
        '--fa-secondary-color': green[600]
    },
    headerError: {
        '--fa-primary-color': theme.palette.error.dark,
        '--fa-secondary-color': theme.palette.error.dark
    }
}))

const IifFileView: React.FunctionComponent<IIifFileViewProps> = ({
    fileIndex
}) => {
    const file = useIifImportSelector(state => IifImportSelectors.selectFile(state, fileIndex));
    const importStatus = useIifImportSelector(state => IifImportSelectors.selectImportStatus(state, fileIndex));
    const loadStatus = useIifImportSelector(state => IifImportSelectors.selectLoadStatus(state, fileIndex));

    const dispatch = useDispatch();

    const [expanded, setExpanded] = React.useState(true);

    const [ignoreFinancialYear, setIgnoreFinancialYear] = React.useState(false);

    React.useEffect(() => {
        setIgnoreFinancialYear(false)
    }, [file, setIgnoreFinancialYear])

    const onChange = React.useCallback((event: React.SyntheticEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded);
    }, [])

    const classes = useStyles();

    const onRefreshClick = React.useCallback((event: React.SyntheticEvent<SVGElement>) => {
        event.stopPropagation();

        if (file?.filename) {
            dispatch(IifImportActions.refreshFile(file?.filename, fileIndex))
        }
    }, [dispatch, fileIndex, file?.filename]);

    const onClick = React.useCallback(() => {
        dispatch(IifImportActions.import(fileIndex))
    }, [dispatch, fileIndex]);

    const onFinancialYearIgnored = React.useCallback((ignored: boolean) => {
        setIgnoreFinancialYear(ignored);
    }, [setIgnoreFinancialYear]);

    if (!file) {
        return null;
    }

    const errors = importStatus && importStatus.responseStatus && importStatus.responseStatus.errors ? importStatus.responseStatus.errors : undefined;

    const invalidFinancialYear = (file.financialYearErrors > 0 && !ignoreFinancialYear);
    const invalid = file.billErrors || file.expenseErrors || file.salesReceiptErrors || file.depositErrors || file.attachmentErrors || !file.attachmentsDownloaded || file.journalEntryErrors;

    return (
        <ExpansionPanel expanded={expanded} onChange={onChange} className={classNames({ [classes.invalid]: invalid || invalidFinancialYear })}>
            <ExpansionPanelSummary
                expandIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth />}
                aria-controls={`file-${fileIndex}-content`}
                id={`file-${fileIndex}-header`}
            >
                <div className={classes.header}>
                    <Typography variant="h2">{file.filename}</Typography>
                    {
                        !importStatus || (importStatus.state !== RequestState.Success && importStatus.state !== RequestState.Failure) && (
                            <FontAwesomeIcon className={classNames(classes.headerIcon, classes.headerRefresh)} spin={loadStatus.state === RequestState.Pending} onClick={onRefreshClick} size="2x" icon={faSync} fixedWidth />
                        )
                    }
                    {
                        !expanded && !!importStatus && importStatus.state === RequestState.Success && (
                            <FontAwesomeIcon className={classNames(classes.headerIcon, classes.headerSuccess)} size="2x" icon={faCheckCircle} fixedWidth />
                        )
                    }
                    {
                        !expanded && !!importStatus && importStatus.state === RequestState.Failure && (
                            <FontAwesomeIcon className={classNames(classes.headerIcon, classes.headerError)} size="2x" icon={faExclamationCircle} fixedWidth />
                        )
                    }
                </div>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
                <div className={classes.contentContainer}>
                    <BillsView
                        fileIndex={fileIndex}
                        billView={file.billView}
                        amount={file.billAmount ?? 0}
                        tax={file.billTax ?? 0}
                        total={file.billTotal ?? 0}
                        errors={errors}
                    />
                    <ExpensesView
                        fileIndex={fileIndex}
                        expenseView={file.expenseView}
                        amount={file.expenseAmount ?? 0}
                        tax={file.expenseTax ?? 0}
                        total={file.expenseTotal ?? 0}
                        errors={errors}
                    />
                    <SalesReceiptsView
                        fileIndex={fileIndex}
                        salesReceiptView={file.salesReceiptView}
                        amount={file.salesReceiptAmount ?? 0}
                        tax={file.salesReceiptTax ?? 0}
                        total={file.salesReceiptTotal ?? 0}
                        errors={errors}
                    />
                    <DepositsView
                        fileIndex={fileIndex}
                        depositView={file.depositView}
                        amount={file.depositAmount ?? 0}
                        tax={file.depositTax ?? 0}
                        total={file.depositTotal ?? 0}
                        errors={errors}
                    />
                    <JournalEntriesView
                        fileIndex={fileIndex}
                        journalEntryView={file.journalEntryView}
                        amount={file.journalEntryAmount ?? 0}
                        tax={file.journalEntryTax ?? 0}
                        total={file.journalEntryTotal ?? 0}
                        errors={errors}
                    />
                    {
                        !invalid && !!loadStatus && loadStatus.state === RequestState.Success && file.financialYearErrors > 0 && !!importStatus && (importStatus.state === RequestState.None || importStatus.state === RequestState.Pending) && (
                            <WarningView
                                title="Not in Financial Year"
                                message={`${file.financialYearErrors} transactions occur before the current financial year.`}
                                onIgnore={onFinancialYearIgnored}
                            />
                        )
                    }
                    {
                        !invalid && !invalidFinancialYear && !!loadStatus && loadStatus.state === RequestState.Success && !!importStatus && (importStatus.state === RequestState.None || importStatus.state === RequestState.Pending) && (
                            <div className={classes.buttonContainer}>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={onClick}
                                    startIcon={
                                        importStatus && importStatus.state === RequestState.Pending ?
                                            <FontAwesomeIcon icon={faSpinner} pulse fixedWidth /> :
                                            null
                                    }
                                    disabled={(importStatus && importStatus.state === RequestState.Pending)}
                                >
                                    Import
                                </Button>
                            </div>
                        )
                    }
                    {
                        !!importStatus && importStatus.state === RequestState.Success && (
                            <SnackbarContent
                                className={classes.success}
                                message={
                                    <span className={classes.message}>
                                        <FontAwesomeIcon className={classes.messageIcon} size="2x" icon={faCheckCircle} fixedWidth />
                                        <Typography variant="h3">
                                            File Imported Successfully
                                        </Typography>
                                    </span>
                                }
                            />
                        )
                    }
                    {
                        !!importStatus && importStatus.state === RequestState.Success && !file.attachmentsImported && file.attachmentErrors && (
                            <ErrorView
                                title="File Attachments Failed to Import"
                                message="Check individual attachment lines for errors."
                            />
                        )
                    }
                    {
                        !!importStatus && importStatus.state === RequestState.Failure && (
                            <ErrorView
                                title="File Import Failed"
                                message="Transactions highlighted in red have not been imported."
                                responseStatus={importStatus.responseStatus}
                            />
                        )
                    }
                    {
                        !!loadStatus && loadStatus.state === RequestState.Failure && (

                            <ErrorView
                                title="File Upload Failed"
                                responseStatus={loadStatus.responseStatus}
                            />
                        )
                    }
                </div>
            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
}

export default IifFileView;


// WEBPACK FOOTER //
// ./src/components/IifFileView.tsx